import { getReadableShiftCodeTextColor } from "@/utils/fontSizeUtils";
import { useEffect, useRef, useMemo, type SetStateAction } from "react";
import type * as type from "@vTypes";
import useRosterConfig from "@/hooks/useRosterConfig";
import { shiftGbName } from "@/utils/shiftgb-name";
import { buildShiftIdToGroupMainLetterMap } from "@/utils/shift-util";

function sortShiftsBySequence<
  T extends { shift_id: string; sequence?: number | null },
>(items: T[]): T[] {
  return [...items].sort(
    (a, b) =>
      (a.sequence ?? 0) - (b.sequence ?? 0) ||
      a.shift_id.localeCompare(b.shift_id)
  );
}

type ColumnGroup = {
  /** shift_gb 원문 (빈 문자열이면 null) */
  shiftGbKey: string | null;
  items: { shift_id: string; color: string }[];
};

type ColumnData = {
  ds: string;
  headerLabel: string;
  groups: ColumnGroup[];
};

/** 모달 열(D/E/N/M)에 넣을 근무구분: shift_gb 그룹 대표 문자 + 직접 매칭 */
function columnLetterForModal(
  s: type.availableShifts,
  groupMap: Map<string, string>,
  columnLetters: string[]
): string | null {
  const fromGroup = groupMap.get(s.shift_id);
  if (fromGroup && columnLetters.includes(fromGroup)) return fromGroup;
  const letter = s.default_shift;
  if (letter && columnLetters.includes(letter)) return letter;
  return null;
}

function buildColumns(
  availableShifts: type.availableShifts[],
  defaultShiftOrder: string[],
  includeMidInGroupMap: boolean
): ColumnData[] {
  const groupMap = buildShiftIdToGroupMainLetterMap(availableShifts, {
    includeMid: includeMidInGroupMap,
  });

  return defaultShiftOrder.map((ds) => {
    const shifts = sortShiftsBySequence(
      availableShifts.filter(
        (s) => columnLetterForModal(s, groupMap, defaultShiftOrder) === ds
      )
    );
    const headerLabel = shifts[0]?.name ?? ds;

    const gbOrder: string[] = [];
    const byGb = new Map<string, typeof shifts>();
    for (const s of shifts) {
      const gb = s.shift_gb ?? "";
      if (!byGb.has(gb)) {
        byGb.set(gb, []);
        gbOrder.push(gb);
      }
      byGb.get(gb)!.push(s);
    }

    const groups: ColumnGroup[] = gbOrder.map((gbKey) => ({
      shiftGbKey: gbKey === "" ? null : gbKey,
      items: (byGb.get(gbKey) ?? []).map((s) => ({
        shift_id: s.shift_id,
        color: s.color,
      })),
    }));

    return { ds, headerLabel, groups };
  });
}

function subGroupLabel(shiftGbKey: string | null): string {
  if (shiftGbKey == null || shiftGbKey === "") return "기본";
  return shiftGbName(shiftGbKey);
}

export default function Modal_shiftCodeList_perType({
  availableShifts,
  currentShiftManage,
  onEditShiftCodes,
  setIsShiftCodeModalOpend,
}: {
  availableShifts: type.availableShifts[];
  currentShiftManage: type.ShiftManageObject[];
  onEditShiftCodes?: () => void;
  setIsShiftCodeModalOpend: React.Dispatch<SetStateAction<boolean>>;
}) {
  const { data: serverConfig } = useRosterConfig();
  const isUseMid = serverConfig?.use_mid;

  /** D/E/N(/M) 순 — 열은 default_shift, 열 안에서는 shift_gb로 묶음 */
  const defaultShiftOrder = useMemo(() => {
    const order = ["D", "E", "N"];
    if (isUseMid !== false) order.push("M");
    return order;
  }, [isUseMid]);

  const includeMidInGroupMap = isUseMid !== false;

  const columns = useMemo(() => {
    if (!availableShifts?.length) return [];
    return buildColumns(
      availableShifts,
      defaultShiftOrder,
      includeMidInGroupMap
    );
  }, [availableShifts, defaultShiftOrder, includeMidInGroupMap]);

  const ref_modal_codeList = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    const modal = ref_modal_codeList.current;
    const mainDom = document.querySelector(".c_main__container") as HTMLElement;
    if (!modal || !mainDom) return;

    const modalRect = modal.getBoundingClientRect();

    offsetRef.current = {
      x: e.clientX - modalRect.left,
      y: e.clientY - modalRect.top,
    };

    draggingRef.current = true;
  };
  const handleMouseMove = (e: MouseEvent) => {
    if (!draggingRef.current) return;

    const mainDom = document.querySelector(".c_main__container") as HTMLElement;
    const modal = ref_modal_codeList.current;
    if (!mainDom || !modal) return;

    const mainRect = mainDom.getBoundingClientRect();
    const modalRect = modal.getBoundingClientRect();

    const rawX = e.clientX - mainRect.left - offsetRef.current.x;
    const rawY =
      e.clientY - mainRect.top - offsetRef.current.y + mainDom.scrollTop;

    const clampedX = Math.max(
      0,
      Math.min(rawX, mainRect.width - modalRect.width)
    );
    const clampedY = Math.max(
      0,
      Math.min(rawY, mainRect.height - modalRect.height)
    );

    const xPercent = (clampedX / mainRect.width) * 100;
    const yPercent = (clampedY / mainRect.height) * 100;

    modal.style.left = `${xPercent}%`;
    modal.style.top = `${yPercent}%`;
    modal.style.transform = "none";
  };
  const handleMouseUp = () => {
    draggingRef.current = false;
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  if (!currentShiftManage) return null;

  return (
    <section
      className="modal_window modal_shiftCodeList"
      ref={ref_modal_codeList}
    >
      <header
        className="modal_header modal_header--bgGray modal_moveHandler"
        onMouseDown={handleMouseDown}
      >
        <div
          className={`shiftCodeList_row grid ${isUseMid !== false ? "grid-cols-4" : "grid-cols-3"}`}
        >
          {columns.map((col) => (
            <div key={col.ds} className="shiftCodeList_category">
              {col.headerLabel}
            </div>
          ))}
        </div>
        <button
          type="button"
          className="modal_button_close"
          onClick={() => {
            setIsShiftCodeModalOpend(false);
          }}
        ></button>
      </header>
      <div className="shiftCodeList_body">
        <div
          className={`grid ${isUseMid !== false ? "grid-cols-4" : "grid-cols-3"}`}
        >
          {columns.map((col) => (
            <div
              key={col.ds}
              className="shiftCodeList_gridWrap !flex !flex-col gap-2"
            >
              {col.groups.map((group, gi) => (
                <div
                  key={`${col.ds}-gb-${group.shiftGbKey ?? "empty"}-${gi}`}
                  className="flex flex-col gap-1.5"
                >
                  {col.groups.length > 1 && (
                    <div className="text-sm font-medium leading-tight text-muted-foreground text-center px-1">
                      {subGroupLabel(group.shiftGbKey)}
                    </div>
                  )}
                  <div className="grid grid-cols-5 gap-2">
                    {group.items.map((item) => (
                      <div
                        key={item.shift_id}
                        className="conf_shiftDisplay"
                        style={{ backgroundColor: item.color, color: getReadableShiftCodeTextColor(item.color) }}
                      >
                        {item.shift_id}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="shiftCodeList_buttonGroup">
          <button
            type="button"
            className="shiftCodeList_textButton"
            onClick={() => {
              onEditShiftCodes?.();
            }}
          >
            근무코드 수정하기
          </button>
        </div>
      </div>
    </section>
  );
}
